export const PatternState = Object.freeze({
  IDLE: 'idle',
  TELEGRAPH: 'telegraph',
  ACTIVE: 'active',
  DONE: 'done',
});

/**
 * Base class for a single boss attack ("move"). Every pattern goes through
 * the same two-beat rhythm the Touhou/Deltarune-style bullet-hell genre
 * relies on for fairness: a readable TELEGRAPH window (flash/outline/lock-on
 * line) that warns the player what's coming, then an ACTIVE window where the
 * attack actually spawns bullets or moves the boss.
 *
 * Subclasses override the `onX` hooks; they never need to touch `state`
 * or timers directly — `update()` drives the state machine.
 */
export class AttackPattern {
  constructor({
    name, telegraphDuration, activeDuration, minPhase = 0, arenaControl = false,
    cardBreakDamage = 0,
  }) {
    this.name = name;
    this.telegraphDuration = telegraphDuration;
    this.activeDuration = activeDuration;
    this.minPhase = minPhase; // lowest boss.phase this pattern is allowed to be picked at
    // Arena-control patterns reserve the player's movement lane. SpawningSystem
    // may layer projectile pressure over one of these, but never stacks two
    // independent forced-position rules at once.
    this.arenaControl = arenaControl;
    // Card HP break (BOSS-REWORK.md §B6). 0 = this card ends on its clock
    // alone, which is every card by default; a positive budget lets the
    // player's damage output end it early and take the capture. Opt-in per
    // card because it moves time-to-kill, which is bench-solved territory.
    this.cardBreakDamage = cardBreakDamage;
    // Per-bullet spawn patch applied by a mastery variant (§B10). Null unless
    // data/patternVariants.js gave this card one and the player has earned it.
    this.bulletOverlay = null;
    this.variantActive = false;
    this.state = PatternState.IDLE;
    this._timer = 0;
  }

  get isDone() {
    return this.state === PatternState.DONE;
  }

  /** Called once by the spawning system when this pattern is selected to run. */
  begin(boss, player) {
    this.state = PatternState.TELEGRAPH;
    this._timer = this.telegraphDuration;
    this.onBegin(boss, player);
  }

  update(dt, boss, player, bulletPool) {
    switch (this.state) {
      case PatternState.TELEGRAPH: {
        this._timer -= dt;
        this.onTelegraphTick(dt, boss, player, bulletPool);
        if (this._timer <= 0) {
          this.state = PatternState.ACTIVE;
          this._timer = this.activeDuration;
          this.onActiveStart(boss, player, bulletPool);
        }
        break;
      }
      case PatternState.ACTIVE: {
        this._timer -= dt;
        this.onActiveTick(dt, boss, player, bulletPool);
        if (this._timer <= 0) {
          this.onEnd(boss, player, bulletPool);
          this.state = PatternState.DONE;
        }
        break;
      }
    }
  }

  /**
   * Ends this card now, as if its clock had run out (BOSS-REWORK.md §B6 — the
   * player broke it). Routed through `onEnd` rather than by dropping the
   * pattern, so a card holding arena state (a wall, a hush, a lock on the
   * boss's position) always gets to take it back down. A card still in its
   * telegraph is simply finished without ever going active — nothing it owns
   * has been put on the board yet.
   */
  forceEnd(boss = null, player = null, bulletPool = null) {
    if (this.state === PatternState.DONE || this.state === PatternState.IDLE) return;
    this.onEnd(boss, player, bulletPool);
    this.state = PatternState.DONE;
  }

  /** Returns a plain-object visual descriptor for rendering, or null. Overridden per-pattern. */
  getVisual(_boss) {
    return null;
  }

  // --- Overridable lifecycle hooks (all no-ops by default) ---
  onBegin(_boss, _player) {}
  onTelegraphTick(_dt, _boss, _player, _bulletPool) {}
  onActiveStart(_boss, _player, _bulletPool) {}
  onActiveTick(_dt, _boss, _player, _bulletPool) {}
  onEnd(_boss, _player, _bulletPool) {}
}
